'use client';

import { c as _c } from "react/compiler-runtime";
import { format, isValid, parse } from 'date-fns';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ItemCard, ItemCardBody, ItemCardDivider, ItemCardHeader, ItemCardSection, ItemCardSummary } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import { GraphQLCardExpiryStatus, GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { slugify } from '@sola/utils/string.ts';
import { PaymentMethodIcon } from '../../atoms/payment-method-icon/payment-method-icon.tsx';
import { getPaymentMethodLabel } from '../../utils/payment-methods.ts';
import { CommitmentAvatarGroup } from '../commitment-avatar-group/commitment-avatar-group.tsx';
import { PaymentMethodCardFooter } from './payment-method-card-footer.tsx';

/**
 * Get payment method details.
 */
const getDetails = (paymentMethod: GraphQLSupporterPaymentMethod, t: TFunction) => {
  if (paymentMethod.creditCard) {
    const expiryDate = paymentMethod.creditCard.expirationDate ? parse(paymentMethod.creditCard.expirationDate, 'MM/yyyy', new Date()) : null;
    if (!isValid(expiryDate)) {
      return;
    }
    if (paymentMethod.creditCard.expiryStatus === 'EXPIRED') {
      return t('payments.methodDetails.expiredCard', {
        expirationDate: format(expiryDate!, 'M/yy'),
        interpolation: {
          escapeValue: false
        }
      });
    }
    return t('payments.methodDetails.card', {
      expirationDate: format(expiryDate!, 'M/yy'),
      interpolation: {
        escapeValue: false
      }
    });
  }
  if (paymentMethod.ach) {
    return paymentMethod.ach.accountName;
  }
};
export type PaymentMethodCardProps = {
  paymentMethodCommitments?: GraphQLCommitment[];
  paymentMethod: GraphQLSupporterPaymentMethod;
  allCommitments?: GraphQLCommitment[];
};

/**
 * Get payment card data.
 */
export const usePaymentMethodCardData = t0 => {
  const $ = _c(15);
  const {
    paymentMethod,
    paymentMethodCommitments
  } = t0;
  const {
    t
  } = useTranslation("my");
  let t1;
  if ($[0] !== paymentMethod || $[1] !== t) {
    t1 = getPaymentMethodLabel(paymentMethod, t);
    $[0] = paymentMethod;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== paymentMethod || $[4] !== t) {
    t2 = getDetails(paymentMethod, t);
    $[3] = paymentMethod;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== paymentMethodCommitments) {
    t3 = paymentMethodCommitments?.length ? paymentMethodCommitments.filter(_temp).sort(_temp2) : [];
    $[6] = paymentMethodCommitments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = paymentMethod.creditCard?.expiryStatus === GraphQLCardExpiryStatus.ExpiringSoon;
  const t5 = paymentMethod.creditCard?.expiryStatus === GraphQLCardExpiryStatus.Expired;
  let t6;
  if ($[8] !== t || $[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = {
      t,
      title: t1,
      label: t2,
      recurringCommitments: t3,
      isWarning: t4,
      isError: t5
    };
    $[8] = t;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const getDefaultHeading = (paymentMethod: GraphQLSupporterPaymentMethod, t: TFunction) => paymentMethod.creditCard ? t('my:payments.methodTypes.creditCard.long') : t('my:payments.methodTypes.ach.long');
export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = props => {
  const {
    title,
    label,
    isError,
    isWarning,
    recurringCommitments,
    t
  } = usePaymentMethodCardData(props);
  const {
    paymentMethod,
    allCommitments
  } = props;
  const {
    accountNickname
  } = paymentMethod;
  const badgeProps: BadgeProps | null = useMemo(() => {
    const expStatus = paymentMethod.creditCard?.expiryStatus;
    if (!expStatus) {
      return null;
    }
    switch (expStatus) {
      case GraphQLCardExpiryStatus.ExpiringSoon:
        return {
          color: 'warning',
          iconBefore: 'alert-triangle',
          label: t('cardExpiring')
        };
      case GraphQLCardExpiryStatus.Expired:
        return {
          color: 'negative',
          iconBefore: 'alert-circle',
          label: t('cardExpired')
        };
    }
  }, [paymentMethod.creditCard?.expiryStatus]);
  return <ItemCard className={cn(`PaymentMethodCard `, {
    'PaymentMethodCard--warning': isWarning,
    'PaymentMethodCard--error': isError,
    [`PaymentMethodCard--${slugify(paymentMethod.paymentType!)}`]: !!paymentMethod.paymentType
  })}
  // Easy code > debatable rule...
  // eslint-disable-next-line no-nested-ternary
  color={isWarning ? 'warning' : isError ? 'negative' : undefined}>
      <ItemCardSection>
        <ItemCardHeader title={accountNickname || getDefaultHeading(paymentMethod, t)} badge={badgeProps ? <Badge responsive {...badgeProps} /> : undefined} />
        <ItemCardBody>
          <ItemCardSummary icon={<PaymentMethodIcon paymentMethod={paymentMethod} />} title={title} subTitle={label} isWarning={isWarning} isError={isError} isSubtleTitle />
          {recurringCommitments?.length ? <CommitmentAvatarGroup commitments={recurringCommitments} tooltipMessage={t('payments.methodCommitmentsTooltip', {
          count: recurringCommitments.length
        })} /> : undefined}
        </ItemCardBody>
      </ItemCardSection>
      <ItemCardDivider />
      <PaymentMethodCardFooter recurringCommitments={recurringCommitments} allCommitments={allCommitments} paymentMethod={paymentMethod} />
    </ItemCard>;
};
function _temp(commitment) {
  return commitment.isRecurring && commitment.status === CommitmentStatusEnum.Active;
}
function _temp2(a, b) {
  const sponsorship = CommitmentTypeEnum.ChildSponsorship;
  if (a.commitmentType === sponsorship && b.commitmentType !== sponsorship) {
    return -1;
  }
  if (a.commitmentType !== sponsorship && b.commitmentType === sponsorship) {
    return 1;
  }
  return 0;
}